import { db } from './firebase'; // Firebase setup file
import { collection, orderBy, addDoc, getDocs, updateDoc, doc, query, where, limit, startAfter, getCountFromServer, writeBatch, serverTimestamp } from 'firebase/firestore';
import { usesLocalApi } from '../services/deploymentConfig';
import { localServiceService } from '../services/serviceLocalApiService';
import { localSettingsService } from '../services/settingsLocalApiService';
import { localSchemeService } from '../services/schemeLocalApiService';

const SHARED_SCHEME_GROUPS = {
  "gohealth-gombe": "CH-GM",
};

const resolveSharedHospitalIds = (hospitalId, schemeId) => {
  const prefix = SHARED_SCHEME_GROUPS[schemeId];
  if (!prefix || !hospitalId || !String(hospitalId).startsWith(prefix)) {
    return hospitalId ? [hospitalId] : [];
  }
  if (hospitalId === prefix) return [prefix];
  return [hospitalId, prefix];
};

const toKobo = (value) => Math.round((Number(value) || 0) * 100);
const fromKobo = (value) => Number(((Number(value) || 0) / 100).toFixed(2));

const isActiveService = (service = {}) => {
  const status = String(service.status || "").trim().toLowerCase();
  return service.deleted !== true && service.isActive !== false && !["inactive", "disabled", "deleted"].includes(status);
};

const schemeOverrideFromLocal = (row = {}) => ({
  ...row,
  id: row.id,
  serviceName: row.serviceName || "",
  serviceCode: row.serviceCode || "",
  serviceNameLowerCase: String(row.serviceName || "").toLowerCase(),
  schemeTariffPrice: fromKobo(row.fixedPriceKobo),
  price: fromKobo(row.fixedPriceKobo),
  status: row.status === "INACTIVE" ? "Inactive" : "Active",
  type: "scheme",
});

const schemeServicePayloadToLocal = (data = {}) => ({
  planId: data.planId || data.schemePlanId || undefined,
  serviceId: data.serviceId || data.documentId || undefined,
  serviceCode: data.serviceCode || data.code || undefined,
  serviceName: data.serviceName || data.name || "",
  coveragePercent: data.coveragePercent !== undefined ? Number(data.coveragePercent || 0) : undefined,
  fixedPriceKobo: data.fixedPriceKobo !== undefined ? Number(data.fixedPriceKobo || 0) : toKobo(data.schemeTariffPrice ?? data.price ?? data.amount),
  status: String(data.status || "Active").toLowerCase() === "inactive" ? "INACTIVE" : "ACTIVE",
  metadata: data.metadata || undefined,
});


/**
 * Add a new service.
 * @param {string} hospitalId - The hospital ID.
 * @param {object} serviceData - Data for the new service.
 * @returns {Promise<object>} - Response containing success status and the new service ID.
 */
export const addService = async (hospitalId, serviceData, user) => {
  if (usesLocalApi()) {
    if (serviceData?.schemeId || serviceData?.type === "scheme") {
      const saved = await localSchemeService.createServiceOverride(
        hospitalId,
        serviceData.schemeId,
        schemeServicePayloadToLocal(serviceData)
      );
      return { success: true, id: saved.id, data: schemeOverrideFromLocal(saved) };
    }
    return localServiceService.addService(hospitalId, serviceData, user);
  }

  try {
    if (!hospitalId) {
      throw new Error('Hospital ID is required.');
    }

    const serviceRef = collection(db, 'services');
    const response = await addDoc(serviceRef, {
      ...serviceData,
      serviceNameLowerCase: serviceData.serviceName.toLowerCase(), // Add lowercase version for case-insensitive search
      hospitalId, // Associate the service with the hospital
      status: serviceData.status || 'Active',
      isActive: serviceData.isActive !== false,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdAtClientMs: Date.now(),
      user,
    });
    return { success: true, id: response.id };
  } catch (error) {
    console.error('Error adding service:', error);
    throw new Error('Failed to add service.');
  }
};

/**
 * Update an existing service.
 * @param {string} serviceId - The ID of the service to update.
 * @param {object} updatedData - Updated data for the service.
 * @returns {Promise<object>} - Response containing success status.
 */
export const updateService = async (serviceId, updatedData) => {
  if (usesLocalApi()) {
    if (updatedData?.schemeId || updatedData?.type === "scheme") {
      const hospitalId = updatedData.hospitalId || sessionStorage.getItem("hospitalId") || localStorage.getItem("hospitalId") || "";
      const saved = await localSchemeService.updateServiceOverride(
        hospitalId,
        updatedData.schemeId,
        serviceId,
        schemeServicePayloadToLocal(updatedData)
      );
      return { success: true, data: schemeOverrideFromLocal(saved) };
    }
    return localServiceService.updateService(serviceId, updatedData);
  }

  try {
    if (!serviceId) {
      throw new Error('Service ID is required.');
    }

    const serviceDoc = doc(db, 'services', serviceId);
    await updateDoc(serviceDoc, {
      ...updatedData,
      ...(updatedData.serviceName ? { serviceNameLowerCase: updatedData.serviceName.toLowerCase() } : {}),
      status: updatedData.status || 'Active',
      isActive: updatedData.isActive !== false,
      deleted: false,
      updatedAt: new Date(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating service:', error);
    throw new Error('Failed to update service.');
  }
};

/**
 * Fetch all services for a specific hospital
 * @param {string} hospitalId - The ID of the hospital.
 * @returns {Array} - List of services.
 */
export const fetchServices = async (hospitalId) => {
  if (usesLocalApi()) {
    return localServiceService.fetchServices(hospitalId);
  }

  try {
    if (!hospitalId) {
      throw new Error('Hospital ID is required.');
    }

    const serviceRef = collection(db, 'services');
    const q = query(serviceRef, where('hospitalId', '==', hospitalId));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter(isActiveService);
  } catch (error) {
    console.error('Error fetching services:', error);
    throw new Error('Failed to fetch services.');
  }
};

/**
 * Fetch all patients for a specific hospital with pagination.
 * @param {string} hospitalId - The hospital ID to filter patients.
 * @param {number} pageSize - The number of patients to fetch per page.
 * @param {object} lastDoc - The last document from the previous query (for pagination).
 * @returns {Promise<object>} - A promise resolving to an object with patient data and pagination info.
 */
export const fetchServicesPaginated = async (hospitalId, pageSize, lastDoc = null, type = "general") => {
  if (usesLocalApi()) {
    return localServiceService.fetchServicesPaginated(hospitalId, pageSize, lastDoc, type);
  }

  try {
    if (!hospitalId) {
      throw new Error("Hospital ID is required to fetch patients.");
    }

    const servicesCollection = collection(db, "services");
    const queryConstraints = [where("hospitalId", "==", hospitalId), where("type", "==", type), limit(pageSize)];
    if (lastDoc) queryConstraints.push(startAfter(lastDoc));

    const q = query(servicesCollection, ...queryConstraints);
    const querySnapshot = await getDocs(q);

    const services = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })).filter(isActiveService);

    return {
      services,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
      totalServices: services.length,
      hasMore: querySnapshot.docs.length === pageSize,
    };
  } catch (error) {
    console.error("Error fetching services:", error.message);
    throw new Error("Failed to fetch services. " + error.message);
  }
};

/**
 * Fetches paginated NHIA services from the "services" collection.
 * @param {string} hospitalId - Only needed to satisfy your calling pattern; not used here.
 * @param {number} pageSize - Number of records per page.
 * @param {DocumentSnapshot} lastDoc - Optional Firestore document snapshot for pagination.
 */
export const fetchServicesPaginatedNHIA = async (pageSize, lastDoc = null) => {
  if (usesLocalApi()) {
    return { services: [], lastDoc: null, totalServices: 0, hasMore: false };
  }

  try {
    const servicesCollection = collection(db, "services");

    // Build query with ordering and pagination
    const queryConstraints = [
      where("type", "==", "nhia"),
      orderBy("serviceCode"),
      limit(pageSize),
    ];
    if (lastDoc) queryConstraints.push(startAfter(lastDoc));

    const q = query(servicesCollection, ...queryConstraints);
    const querySnapshot = await getDocs(q);

    const services = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })).filter(isActiveService);

    // Count total NHIA records (not paginated)
    const totalQuery = query(servicesCollection, where("type", "==", "nhia"));
    const totalSnapshot = await getCountFromServer(totalQuery);

    return {
      services,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
      totalServices: totalSnapshot.data().count,
      hasMore: querySnapshot.docs.length === pageSize,
    };
  } catch (error) {
    console.error("Error fetching NHIA services:", error.message);
    throw new Error("Failed to fetch NHIA services. " + error.message);
  }
};

/**
 * Fetch paginated scheme services (non-NHIA).
 * @param {string} hospitalId
 * @param {string} schemeId
 * @param {number} pageSize
 * @param {DocumentSnapshot} lastDoc
 */
export const fetchSchemeServicesPaginated = async (hospitalId, schemeId, pageSize, lastDoc = null) => {
  if (usesLocalApi()) {
    const rows = await localSchemeService.listServiceOverrides(hospitalId, schemeId);
    const services = (Array.isArray(rows) ? rows : []).map(schemeOverrideFromLocal).filter((row) => row.status !== "Inactive");
    return {
      services: services.slice(0, pageSize || services.length),
      lastDoc: null,
      totalServices: services.length,
      hasMore: false,
    };
  }

  try {
    if (!hospitalId) {
      throw new Error("Hospital ID is required.");
    }
    if (!schemeId) {
      throw new Error("Scheme ID is required.");
    }

    if (schemeId === "nhia") {
      return await fetchServicesPaginatedNHIA(pageSize, lastDoc);
    }

    const servicesCollection = collection(db, "services");
    const sharedHospitalIds = resolveSharedHospitalIds(hospitalId, schemeId);
    const hospitalFilter =
      sharedHospitalIds.length > 1
        ? where("hospitalId", "in", sharedHospitalIds)
        : where("hospitalId", "==", hospitalId);
    const queryConstraints = [
      hospitalFilter,
      where("schemeId", "==", schemeId),
      where("type", "==", "scheme"),
      orderBy("serviceNameLowerCase"),
      limit(pageSize),
    ];
    if (lastDoc) queryConstraints.push(startAfter(lastDoc));

    const q = query(servicesCollection, ...queryConstraints);
    const querySnapshot = await getDocs(q);

    const services = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })).filter(isActiveService);

    const totalQuery = query(
      servicesCollection,
      hospitalFilter,
      where("schemeId", "==", schemeId),
      where("type", "==", "scheme")
    );
    const totalSnapshot = await getCountFromServer(totalQuery);

    return {
      services,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
      totalServices: totalSnapshot.data().count,
      hasMore: querySnapshot.docs.length === pageSize,
    };
  } catch (error) {
    console.error("Error fetching scheme services:", error.message);
    throw new Error("Failed to fetch scheme services. " + error.message);
  }
};

/**
 * Bulk import scheme services (CSV or other sources).
 * @param {string} hospitalId
 * @param {string} schemeId
 * @param {string} schemeName
 * @param {Array} services
 * @param {object} user
 * @param {object} options
 */
export const bulkImportSchemeServices = async (
  hospitalId,
  schemeId,
  schemeName,
  services = [],
  user,
  options = {}
) => {
  if (usesLocalApi()) {
    throw new Error("Local API scheme service import is not available yet.");
  }

  if (!hospitalId) throw new Error("Hospital ID is required.");
  if (!schemeId) throw new Error("Scheme ID is required.");

  const { batchSize = 400, onProgress } = options;
  const total = services.length;
  let imported = 0;

  for (let i = 0; i < services.length; i += batchSize) {
    const batch = writeBatch(db);
    const slice = services.slice(i, i + batchSize);
    const validSlice = slice.filter((service) => String(service?.serviceName || "").trim());
    const now = new Date();
    const clientMs = Date.now();

    validSlice.forEach((service) => {
      const name = String(service?.serviceName || "").trim();
      if (!name) return;

      const docRef = doc(collection(db, "services"));
      batch.set(docRef, {
        ...service,
        serviceName: name,
        serviceNameLowerCase: name.toLowerCase(),
        hospitalId,
        schemeId,
        schemeName,
        type: "scheme",
        createdAt: now,
        updatedAt: now,
        createdAtClientMs: clientMs,
        user,
      });
    });

    await batch.commit();
    imported += validSlice.length;
    if (typeof onProgress === "function") {
      onProgress({ imported, total });
    }
  }

  return { imported, total };
};


/**
 * Delete a service
 * @param {string} serviceId - The ID of the service to delete.
 * @returns {object} - Response with success status.
 */
export const deleteService = async (serviceId) => {
  if (usesLocalApi()) {
    return localServiceService.deleteService(serviceId);
  }

  try {
    if (!serviceId) {
      throw new Error('Service ID is required.');
    }

    const serviceDoc = doc(db, 'services', serviceId);
    await updateDoc(serviceDoc, {
      status: 'Inactive',
      isActive: false,
      deleted: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting service:', error);
    throw new Error('Failed to delete service.');
  }
};



// Fetch services by departmentId
export const fetchServicesByDepartment = async (departmentId) => {
  if (usesLocalApi()) {
    return localServiceService.fetchServicesByDepartment(departmentId);
  }

  try {
    if (!departmentId) throw new Error('Department ID is required.');

    const serviceRef = collection(db, 'services');
    const q = query(serviceRef, where('departmentId', '==', departmentId));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter(isActiveService);
  } catch (error) {
    console.error('Error fetching services by department:', error.message);
    throw new Error('Failed to fetch services by department.');
  }
};




// Fetch services filtered by hospitalId, optional departmentId, and serviceName(s)
export const fetchFilteredServices = async (hospitalId, departmentId, param) => {
  if (usesLocalApi()) {
    return localServiceService.fetchFilteredServices(hospitalId, departmentId, param);
  }

  try {
    const serviceRef = collection(db, "services");

    let q = query(serviceRef, where("hospitalId", "==", hospitalId));

    // Conditionally add department filter
    if (departmentId) {
      q = query(q, where("departmentId", "==", departmentId));
    }


    // Add serviceNameLowerCase filter if param is an array
    if (Array.isArray(param) && param.length > 0) {
      q = query(q, where("serviceNameLowerCase", "in", param));
    }


    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter(isActiveService);
  } catch (error) {
    console.error("Error fetching filtered services:", error.message);
    throw new Error("Failed to fetch filtered services.");
  }
};


export const fetchFilteredServicesGeneral = async (hospitalId, param) => {
  if (usesLocalApi()) {
    return localServiceService.fetchFilteredServicesGeneral(hospitalId, param);
  }

  try {
    if (!param) throw new Error('param is required.');



    const serviceRef = collection(db, 'services');
    const q = query(
      serviceRef,
      where('hospitalId', '==', hospitalId),
      where('serviceNameLowerCase', 'in', param)
    );
    const snapshot = await getDocs(q);
    const services = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter(isActiveService);


    const filtered = services.filter((service) => service.type !== 'scheme');


    return filtered;
  } catch (error) {
    console.error('Error fetching filtered services:', error.message);
    throw new Error('Failed to fetch filtered services.');
  }
};

// Fetch services by schemeId and name filters
export const fetchFilteredSchemeServices = async (schemeId, param, hospitalId = null) => {
  if (usesLocalApi()) {
    const rows = await localSchemeService.listServiceOverrides(hospitalId || sessionStorage.getItem("hospitalId") || "", schemeId);
    const wanted = (Array.isArray(param) ? param : []).map((item) => String(item || "").toLowerCase());
    return rows.map(schemeOverrideFromLocal).filter((row) => !wanted.length || wanted.includes(row.serviceNameLowerCase));
  }

  try {
    if (!schemeId) throw new Error('Scheme ID is required.');

    const serviceRef = collection(db, 'services');
    let q;

    if (schemeId === "nhia") {
      q = query(
        serviceRef,
        where('schemeId', '==', schemeId),
        where('serviceNameLowerCase', 'in', param)
      );
    } else if (hospitalId) {
      const sharedHospitalIds = resolveSharedHospitalIds(hospitalId, schemeId);
      const hospitalFilter =
        sharedHospitalIds.length > 1
          ? where("hospitalId", "in", sharedHospitalIds)
          : where("hospitalId", "==", hospitalId);
      q = query(
        serviceRef,
        hospitalFilter,
        where('schemeId', '==', schemeId),
        where('serviceNameLowerCase', 'in', param)
      );
    } else {
      q = query(
        serviceRef,
        where('schemeId', '==', schemeId),
        where('serviceNameLowerCase', 'in', param)
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter(isActiveService);
  } catch (error) {
    console.error('Error fetching filtered services:', error.message);
    throw new Error('Failed to fetch filtered services.');
  }
};



/**
 * Search services by service name.
 * @param {string} searchTerm - The term to search for.
 * @param {string} hospitalId - The hospital ID for filtering services.
 * @returns {Promise<Array>} - Array of matching services.
 */
export const searchServicesByName = async (searchTerm, hospitalId, type = "general") => {
  if (usesLocalApi()) {
    return localServiceService.searchServicesByName(searchTerm, hospitalId, type);
  }

  try {
    if (!searchTerm.trim()) {
      throw new Error("Search term is required.");
    }
    const lowerCaseSearchTerm = searchTerm.trim().toLowerCase(); // Convert search term to lowercase

    const servicesRef = collection(db, "services");
    const q = query(
      servicesRef,
      where("hospitalId", "==", hospitalId), // Ensure hospital filtering
      where("type", "==", type), // Add type filter
      where("serviceNameLowerCase", ">=", lowerCaseSearchTerm),
      where("serviceNameLowerCase", "<=", lowerCaseSearchTerm + "\uf8ff")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })).filter(isActiveService);
  } catch (error) {
    console.error("Error searching services:", error.message);
    throw new Error("Failed to search services. " + error.message);
  }
};

export const searchServicesNHIA = async (searchTerm) => {
  if (usesLocalApi()) {
    const hospitalId = sessionStorage.getItem("hospitalId") || localStorage.getItem("hospitalId") || "";
    const schemes = await localSchemeService.fetchCustomSchemeActive(hospitalId);
    const nhia = schemes.find((scheme) => String(scheme.type || scheme.name).toUpperCase().includes("NHIA"));
    if (!nhia) return [];
    return searchSchemeServices(hospitalId, nhia.id, searchTerm);
  }

  try {
    if (!searchTerm.trim()) {
      throw new Error("Search term is required.");
    }

    const lowerCaseSearchTerm = searchTerm.trim().toLowerCase();
    const servicesRef = collection(db, "services");

    const q = query(
      servicesRef,
      where("type", "==", "nhia"),
      where("serviceNameLowerCase", ">=", lowerCaseSearchTerm),
      where("serviceNameLowerCase", "<=", lowerCaseSearchTerm + "\uf8ff"),
      limit(20)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })).filter(isActiveService);

  } catch (error) {
    console.error("Error searching NHIA services:", error.message);
    throw new Error("Failed to search NHIA services. " + error.message);
  }
};

/**
 * Search scheme services (non-NHIA).
 */
export const searchSchemeServices = async (hospitalId, schemeId, searchTerm) => {
  if (usesLocalApi()) {
    const term = String(searchTerm || "").trim().toLowerCase();
    const rows = await localSchemeService.listServiceOverrides(hospitalId, schemeId);
    return rows
      .map(schemeOverrideFromLocal)
      .filter((row) => !term || row.serviceNameLowerCase.includes(term) || String(row.serviceCode || "").toLowerCase().includes(term));
  }

  try {
    if (!hospitalId) throw new Error("Hospital ID is required.");
    if (!schemeId) throw new Error("Scheme ID is required.");
    if (!searchTerm.trim()) throw new Error("Search term is required.");

    if (schemeId === "nhia") {
      return await searchServicesNHIA(searchTerm);
    }

    const lowerCaseSearchTerm = searchTerm.trim().toLowerCase();
    const servicesRef = collection(db, "services");
    const sharedHospitalIds = resolveSharedHospitalIds(hospitalId, schemeId);
    const hospitalFilter =
      sharedHospitalIds.length > 1
        ? where("hospitalId", "in", sharedHospitalIds)
        : where("hospitalId", "==", hospitalId);

    const q = query(
      servicesRef,
      hospitalFilter,
      where("schemeId", "==", schemeId),
      where("type", "==", "scheme"),
      where("serviceNameLowerCase", ">=", lowerCaseSearchTerm),
      where("serviceNameLowerCase", "<=", lowerCaseSearchTerm + "\uf8ff"),
      limit(20)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })).filter(isActiveService);
  } catch (error) {
    console.error("Error searching scheme services:", error.message);
    throw new Error("Failed to search scheme services. " + error.message);
  }
};


/**
 * Search services by name, optionally including scheme services.
 * @param {string} hospitalId
 * @param {string} searchTerm
 * @param {boolean|string} schemeContext
 * @returns {Promise<Array>}
 */
export const searchServicesByNameBilling = async (hospitalId, searchTerm, schemeContext = false) => {
  if (usesLocalApi()) {
    return localServiceService.searchServicesByNameBilling(hospitalId, searchTerm, schemeContext);
  }

  try {
    if (!hospitalId) {
      throw new Error("Hospital ID is required.");

    }    
    const servicesCollection = collection(db, "services");
    const lowerSearchTerm = searchTerm.trim().toLowerCase();

    const schemeId = typeof schemeContext === "string" ? schemeContext : null;
    const includeScheme = schemeContext === true || !!schemeId;

    const fetchHospitalResults = async (filterScheme = false) => {
      const hospitalQuery = query(
        servicesCollection,
        where("hospitalId", "==", hospitalId),
        where("serviceNameLowerCase", ">=", lowerSearchTerm),
        where("serviceNameLowerCase", "<=", lowerSearchTerm + "\uf8ff"),
        limit(20)
      );
      const snap = await getDocs(hospitalQuery);
      const items = snap.docs.map((doc) => ({ id: doc.id, ...doc.data(), source: "hospital" })).filter(isActiveService);
      if (!filterScheme) return items;
      return items.filter((item) => item?.type !== "scheme" && item?.type !== "nhia");
    };

    if (includeScheme) {
      if (!schemeId || schemeId === "nhia") {
        const nhiaQuery = query(
          servicesCollection,
          where("type", "==", "nhia"),
          where("serviceNameLowerCase", ">=", lowerSearchTerm),
          where("serviceNameLowerCase", "<=", lowerSearchTerm + "\uf8ff"),
          limit(20)
        );
        const nhiaSnap = await getDocs(nhiaQuery);
        const nhiaResults = nhiaSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          source: "nhia",
        })).filter(isActiveService);
        if (nhiaResults.length) return nhiaResults;
        return await fetchHospitalResults(true);
      }

      const sharedHospitalIds = resolveSharedHospitalIds(hospitalId, schemeId);
      const hospitalFilter =
        sharedHospitalIds.length > 1
          ? where("hospitalId", "in", sharedHospitalIds)
          : where("hospitalId", "==", hospitalId);
      const schemeQuery = query(
        servicesCollection,
        hospitalFilter,
        where("schemeId", "==", schemeId),
        where("type", "==", "scheme"),
        where("serviceNameLowerCase", ">=", lowerSearchTerm),
        where("serviceNameLowerCase", "<=", lowerSearchTerm + "\uf8ff"),
        limit(20)
      );
      const schemeSnap = await getDocs(schemeQuery);
      const schemeResults = schemeSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        source: "scheme",
      })).filter(isActiveService);
      if (schemeResults.length) return schemeResults;
      return await fetchHospitalResults(true);
    }

    return await fetchHospitalResults(false);
  } catch (error) {
    console.error("Error searching service:", error.message);
    throw new Error("Failed to search service. " + error.message);
  }
};

export const searchServicesByCategory = async (hospitalId, searchTerm, category) => {
  if (usesLocalApi()) {
    return localServiceService.searchServicesByCategory(hospitalId, searchTerm, category);
  }

  try {
    if (!hospitalId) {
      throw new Error("Hospital ID is required.");
    }

    const inventoryCollection = collection(db, "services");
    let q;

    // If searchTerm is provided, perform a case-insensitive search
    if (searchTerm && searchTerm.trim() !== "") {
      const lowerSearchTerm = searchTerm.toLowerCase();
      q = query(
        inventoryCollection,
        where("hospitalId", "==", hospitalId),
        where("serviceCategory", "==", category),
        where("serviceNameLowerCase", ">=", lowerSearchTerm),
        where("serviceNameLowerCase", "<=", lowerSearchTerm + "\uf8ff")
      );
    } else {
      // If no searchTerm, fetch all medications for the hospital
      q = query(inventoryCollection, where("hospitalId", "==", hospitalId), limit(50)); // Limit for safety
    }

    const querySnapshot = await getDocs(q);

    const medications = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })).filter(isActiveService);


    return medications;
  } catch (error) {
    console.error("Error searching service:", error.message);
    throw new Error("Failed to search service. " + error.message);
  }
};

export const addServiceCategory = async (hospitalId, categoryData) => {
  if (usesLocalApi()) {
    return localSettingsService.createServiceCategory(hospitalId, categoryData);
  }
  return { success: false, message: "Firebase service categories are not configured as a separate collection yet." };
};

export const fetchServiceCategories = async (hospitalId, filters = {}) => {
  if (usesLocalApi()) {
    return localSettingsService.fetchServiceCategories(hospitalId, filters);
  }
  const services = await fetchServices(hospitalId);
  const unique = new Map();
  services.forEach((service) => {
    const name = service.serviceCategory || service.category;
    if (!name || unique.has(name)) return;
    unique.set(name, {
      id: name,
      name,
      serviceCategory: name,
      departmentName: service.departmentName || service.department || "",
      status: "Active",
    });
  });
  return Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export const updateServiceCategory = async (hospitalId, id, categoryData) => {
  if (usesLocalApi()) {
    return localSettingsService.updateServiceCategory(hospitalId, id, categoryData);
  }
  return { success: false, message: "Firebase service category updates are not configured as a separate collection yet." };
};

export const deleteServiceCategory = async (hospitalId, id) => {
  if (usesLocalApi()) {
    return localSettingsService.deleteServiceCategory(hospitalId, id);
  }
  return { success: false, message: "Firebase service category deletion is not configured as a separate collection yet." };
};
